import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import axiosClient from "../../../../api-client/AxiosClient";
import { toast } from "react-toastify";
import { Trash2 } from "lucide-react";
import { FaEdit } from "react-icons/fa";
import MultiSelectDropdown from "./MultiSelectDropdown";
// Helper function for button hover handlers
const createHoverHandlers = (hoverColor, defaultColor, hoverTextColor = null, defaultTextColor = null) => ({
  onMouseOver: (e) => {
    e.currentTarget.style.backgroundColor = hoverColor;
    if (hoverTextColor) e.currentTarget.style.color = hoverTextColor;
  },
  onMouseOut: (e) => {
    e.currentTarget.style.backgroundColor = defaultColor;
    if (defaultTextColor) e.currentTarget.style.color = defaultTextColor;
  },
  onFocus: (e) => {
    e.currentTarget.style.backgroundColor = hoverColor;
    if (hoverTextColor) e.currentTarget.style.color = hoverTextColor;
  },
  onBlur: (e) => {
    e.currentTarget.style.backgroundColor = defaultColor;
    if (defaultTextColor) e.currentTarget.style.color = defaultTextColor;
  },
});
// Normalize a saved config's target objects into { id, label } references,
// tolerating both the object-list shape ([{ name, id }]) and the CSV string
// shape ("id1,id2" or "name1,name2") that the API may return.
const buildSavedRefs = (config) => {
  if (Array.isArray(config.lstTargetObjects) && config.lstTargetObjects.length > 0) {
    return config.lstTargetObjects.map((obj) => ({
      id: obj.id ?? obj.object_id ?? obj.objectId ?? null,
      label: obj.name ?? obj.description ?? null,
    }));
  }

  if (typeof config.targetObjects === 'string' && config.targetObjects.trim() !== '') {
    return config.targetObjects
      .split(',')
      .map((token) => token.trim())
      .filter(Boolean)
      .map((token) => ({ id: token, label: token }));
  }

  if (Array.isArray(config.targetObjects)) {
    return config.targetObjects
      .filter(Boolean)
      .map((token) => ({ id: token, label: token }));
  }

  return [];
};

const OrganizationConfigureModal = ({
  isOpen,
  organization,
  onClose,
  isWizardMode = false,
  onBack = null,
}) => {
  const [loading, setLoading] = useState(false);
  const [configurations, setConfigurations] = useState([]);
  const [domains, setDomains] = useState([]);
  const [targets, setTargets] = useState([]);
  const [objects, setObjects] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [isAddConfigModalOpen, setIsAddConfigModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [configToDelete, setConfigToDelete] = useState(null);
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [configFormData, setConfigFormData] = useState({
    domain: "",
    source: "",
    target: "",
    targetObjects: [],
    isActive: true,
    configurationTemplateId: "",
  });
  // Identifies the most recent "open edit" request so a slow object-load can't
  // apply its reconciled selection to a different config opened afterwards.
  const openRequestRef = useRef(0);

  useEffect(() => {
    if (isOpen && organization) {
      fetchDomainsAndTargets();
      if (isWizardMode) {
        // Wizard mode: skip list, go straight to Add form
        handleOpenAddConfigModal();
      } else {
        // Normal mode: load config list
        fetchOrganizationConfigs(organization.organizationId);
      }
    }
  }, [isOpen, organization]);

  const fetchDomainsAndTargets = async () => {
    try {
      const [domainsResponse, targetsResponse, templatesResponse] = await Promise.all([
        axiosClient.get("/api/Domain/all"),
        axiosClient.get("/api/Target/all"),
        axiosClient.get("/api/Organization/GetConfigurationTemplates")
      ]);

      setDomains(domainsResponse.data);
      setTargets(targetsResponse.data);
      if (templatesResponse.data) setTemplates(templatesResponse.data);
    } catch (error) {
      console.error("Error fetching domains and targets:", error);
    }
  };

  const fetchObjectsByTarget = async (targetId) => {
    if (!targetId) {
      setObjects([]);
      return [];
    }

    try {
      const response = await axiosClient.get(
        `/api/Object/GetObjectByTargetId?targetId=${targetId}`
      );
      setObjects(response.data);
      return response.data || [];
    } catch (error) {
      console.error("Error fetching objects:", error);
      setObjects([]);
      return [];
    }
  };

  const fetchOrganizationConfigs = async (orgId) => {
    setLoading(true);
    try {
      const response = await axiosClient.get(
        `/api/Organization/GetOrganizationConfigs?orgId=${orgId}`
      );
      setConfigurations(response.data);
    } catch (error) {
      console.error("Error fetching configurations:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAddConfigModal = async (config = null) => {
    const requestId = openRequestRef.current + 1;
    openRequestRef.current = requestId;

    if (config) {
      setSelectedConfig(config);

      // Collect the saved selection as { id, label } references from whichever
      // shape the API returned it in (object list and/or the id/name CSV string).
      const savedRefs = buildSavedRefs(config);

      setConfigFormData({
        domain: config.domainName,
        source: config.sourceName || "",
        target: config.targetName,
        targetObjects: savedRefs.map(ref => ref.label).filter(Boolean),
        isActive: config.isActive,
        configurationTemplateId: config.configurationTemplateId || "",
      });
      setIsAddConfigModalOpen(true);

      const selectedTarget = targets.find(t => t.name === config.targetName);
      if (selectedTarget) {
        const loadedObjects = await fetchObjectsByTarget(selectedTarget.target_id);

        // Bail out if another config was opened while objects were loading.
        if (requestId !== openRequestRef.current) return;

        // Reconcile the saved selection against the freshly loaded objects so the
        // dropdown's checkbox match (selectedValues.includes(option.name)) lines up,
        // even when the values persisted in the config (id, name or description)
        // differ from the API's names. Drop any saved entry that no longer maps to
        // a loaded object so stale values don't linger as phantom chips.
        if (savedRefs.length > 0 && loadedObjects.length > 0) {
          // Map each saved ref to a real option name, drop entries that no longer
          // exist for this target, and de-duplicate so the selection can never
          // exceed the number of available objects (a config may carry stale
          // entries from when the target had a different object set).
          const reconciled = [
            ...new Set(
              savedRefs
                .map((ref) => {
                  const match =
                    (ref.id != null &&
                      loadedObjects.find(o => String(o.object_id) === String(ref.id))) ||
                    loadedObjects.find(o => o.name === ref.label) ||
                    loadedObjects.find(o => o.description === ref.label);
                  return match ? match.name : null;
                })
                .filter(Boolean)
            ),
          ];

          setConfigFormData(prev => ({ ...prev, targetObjects: reconciled }));
        }
      }
      return;
    }
    {
      setSelectedConfig(null);
      setConfigFormData({
        domain: "",
        source: "",
        target: "",
        targetObjects: [],
        isActive: true,
        configurationTemplateId: "",
      });
      setObjects([]);
    }
    setIsAddConfigModalOpen(true);
  };

  const handleCloseAddConfigModal = () => {
    // Invalidate any in-flight reconciliation from the open that is now closing.
    openRequestRef.current += 1;
    setIsAddConfigModalOpen(false);
    setSelectedConfig(null);
    setConfigFormData({
      domain: "",
      source: "",
      target: "",
      targetObjects: [],
      isActive: true,
      configurationTemplateId: "",
    });
    setObjects([]);
  };

  const handleConfigInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    setConfigFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (name === "target") {
      // The user picked a new target: invalidate any pending reconciliation from
      // the edit-open so it can't overwrite this fresh, empty selection.
      openRequestRef.current += 1;
      const selectedTarget = targets.find(t => t.name === value);
      if (selectedTarget) {
        fetchObjectsByTarget(selectedTarget.target_id);
      } else {
        setObjects([]);
      }
      setConfigFormData((prev) => ({
        ...prev,
        targetObjects: [],
      }));
    }
  };

  const handleTargetObjectsChange = (selectedValues) => {
    setConfigFormData((prev) => ({
      ...prev,
      targetObjects: selectedValues,
    }));
  };

  const handleConfigSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const selectedDomain = domains.find(d => d.name === configFormData.domain);
      const selectedTarget = targets.find(t => t.name === configFormData.target);

      const selectedObjectsData = objects
        .filter(obj => configFormData.targetObjects.includes(obj.name))
        .map(obj => ({
          name: obj.name,
          id: obj.object_id
        }));

      const selectedObjectIds = objects
        .filter(obj => configFormData.targetObjects.includes(obj.name))
        .map(obj => obj.object_id);

      const targetObjectsString = selectedObjectIds.join(',');

      const payload = selectedConfig
        ? {
          organizationConfigId: selectedConfig.organizationConfigId,
          organizationId: organization.organizationId,
          configurationTemplateId: configFormData.configurationTemplateId,
          domain: selectedDomain?.domain_id || configFormData.domain,
          source: configFormData.source,
          target: selectedTarget?.target_id || configFormData.target,
          targetObjects: targetObjectsString,
          lstTargetObjects: selectedObjectsData,
          isActive: configFormData.isActive,
        }
        : {
          organizationConfigId: "string",
          organizationId: organization.organizationId,
          configurationTemplateId: configFormData.configurationTemplateId,
          domain: selectedDomain?.domain_id || configFormData.domain,
          source: configFormData.source,
          target: selectedTarget?.target_id || configFormData.target,
          targetObjects: targetObjectsString,
          lstTargetObjects: selectedObjectsData,
          isActive: configFormData.isActive,
        };

      if (selectedConfig) {
        await axiosClient.put("/api/Organization/UpdateOrganizationConfig", payload);
        toast.success("Configuration updated successfully.");
      } else {
        await axiosClient.post("/api/Organization/CreateOrganizationConfig", payload);
        toast.success("Configuration created successfully.");
      }

      if (isWizardMode) {
        // Close the entire wizard flow
        onClose();
      } else {
        await fetchOrganizationConfigs(organization.organizationId);
        handleCloseAddConfigModal();
      }

    } catch (error) {
      console.error("Error saving configuration:", error);
      const errorMessage =
        error?.response?.data?.message ||
        `Failed to ${selectedConfig ? "update" : "create"} configuration. Please try again.`;
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfig = async (configId) => {
    setConfigToDelete(configId);
    setIsDeleteModalOpen(true);
  };

  const confirmDeleteConfig = async () => {
    // Tracked separately from `loading`: that one drives the list spinner behind
    // this modal, so on its own it leaves the Delete button with no feedback.
    setIsDeleting(true);
    setLoading(true);
    try {
      await axiosClient.delete(
        `/api/Organization/DeleteOrganizationConfig/${configToDelete}`
      );
      toast.success("Configuration deleted successfully.");
      await fetchOrganizationConfigs(organization.organizationId);
    } catch (error) {
      console.error("Error deleting configuration:", error);
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to delete configuration. Please try again.";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
      setConfigToDelete(null);
    }
  };

  const getStatusClassName = (isActive) => {
    return isActive
      ? "bg-green-100 text-green-800"
      : "bg-red-100 text-red-800";
  };

  const getStatusText = (isActive) => {
    return isActive ? "Active" : "Inactive";
  };

  if (!isOpen) return null;

  // Extracted submit button text to avoid nested ternary
  let submitButtonText = "Create";
  if (loading) {
    submitButtonText = "Saving...";
  } else if (selectedConfig) {
    submitButtonText = "Update";
  }

  return (
    <>
      {/* Main Configuration Modal - hidden in wizard mode */}
      {!isWizardMode && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-5xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <div>
                <h3 className="text-xl font-bold text-gray-800">
                  Organization Configurations
                </h3>
                <p className="text-sm text-gray-600 mt-1">
                  {organization?.name}
                </p>
              </div>
              <button
                onClick={() => handleOpenAddConfigModal()}
                className="bg-blue-700 text-white px-4 py-2 rounded-lg font-medium hover:bg-blue-800 transition-colors flex items-center gap-2"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 4v16m8-8H4"
                  />
                </svg>
                Add Configuration
              </button>
            </div>

            <div className="p-6">
              {loading && (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
                </div>
              )}
              {!loading && configurations.length === 0 && (
                <div className="text-center py-12 text-gray-500">
                  No configurations found for this organization
                </div>
              )}
              {!loading && configurations.length > 0 && (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Domain
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Source
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Target
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {configurations.map((config) => (
                        <tr
                          key={config.organizationConfigId}
                          className="hover:bg-gray-50"
                        >
                          <td className="px-4 py-4 text-sm text-gray-700">
                            {config.domainName}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-700">
                            {config.sourceName || "-"}
                          </td>
                          <td className="px-4 py-4 text-sm text-gray-700">
                            {config.targetName}
                          </td>
                          <td className="px-4 py-4">
                            <span
                              className={`px-2.5 py-1 text-xs font-medium rounded-full ${getStatusClassName(config.isActive)}`}
                            >
                              {getStatusText(config.isActive)}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleOpenAddConfigModal(config)}
                                style={{
                                  padding: "8px",
                                  borderRadius: "9999px",
                                  border: "none",
                                  backgroundColor: "transparent",
                                  color: "#3b82f6",
                                  cursor: "pointer",
                                  transition: "all 0.2s",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                                {...createHoverHandlers("#dbeafe", "transparent", "#2563eb", "#3b82f6")}
                                title="Edit Configuration"
                              >
                                <FaEdit size={15} />
                              </button>
                              <button
                                onClick={() =>
                                  handleDeleteConfig(config.organizationConfigId)
                                }
                                style={{
                                  padding: "8px",
                                  borderRadius: "9999px",
                                  border: "none",
                                  backgroundColor: "transparent",
                                  color: "#ef4444",
                                  cursor: "pointer",
                                  transition: "all 0.2s",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                                {...createHoverHandlers("#fee2e2", "transparent", "#dc2626", "#ef4444")}
                                title="Delete Configuration"
                              >
                                <Trash2 size={15} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="p-6 border-t border-gray-200 flex justify-end">
              <button
                onClick={onClose}
                className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Configuration Modal */}
      {isAddConfigModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-xl font-bold text-gray-800">
                {selectedConfig ? "Edit Configuration" : "Add New Configuration"}
              </h3>
            </div>

            <form onSubmit={handleConfigSubmit} className="p-6">
              {/* Step Indicator - only in wizard mode */}
              {isWizardMode && (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", marginBottom: "20px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <div style={{ width: "28px", height: "28px", borderRadius: "50%", backgroundColor: "#e5e7eb", color: "#9ca3af", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "13px", fontWeight: "700" }}>1</div>
                    <div style={{ width: "60px", height: "2px", backgroundColor: "#1e40af" }}></div>
                    <div style={{ width: "28px", height: "28px", borderRadius: "50%", backgroundColor: "#1e40af", color: "white", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "13px", fontWeight: "700" }}>2</div>
                  </div>
                  <span style={{ fontSize: "12px", color: "#6b7280", fontWeight: "500", marginLeft: "12px" }}>Step 2 of 2</span>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label htmlFor="config-domain" className="block text-sm font-medium text-gray-700 mb-1">
                    Domain *
                  </label>
                  <select
                    id="config-domain"
                    name="domain"
                    value={configFormData.domain}
                    onChange={handleConfigInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Domain</option>
                    {domains.map((domain) => (
                      <option key={domain.domain_id} value={domain.name}>
                        {domain.name} - {domain.description}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="config-template" className="block text-sm font-medium text-gray-700 mb-1">
                    Template *
                  </label>
                  <select
                    id="config-template"
                    name="configurationTemplateId"
                    value={configFormData.configurationTemplateId}
                    onChange={handleConfigInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Template</option>
                    {templates.map((template) => (
                      <option key={template.configurationTemplateId} value={template.configurationTemplateId}>
                        {template.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="config-target" className="block text-sm font-medium text-gray-700 mb-1">
                    Target *
                  </label>
                  <select
                    id="config-target"
                    name="target"
                    value={configFormData.target}
                    onChange={handleConfigInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select Target</option>
                    {targets.map((target) => (
                      <option key={target.target_id} value={target.name}>
                        {target.name} - {target.description}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label htmlFor="config-target-objects" className="block text-sm font-medium text-gray-700 mb-1">
                    Target Objects
                  </label>
                  <div id="config-target-objects">
                    <MultiSelectDropdown
                      options={objects}
                      selectedValues={configFormData.targetObjects}
                      onChange={handleTargetObjectsChange}
                      disabled={!configFormData.target}
                      placeholder={configFormData.target ? "Select target objects" : "Select a target first"}
                    />
                  </div>
                  {configFormData.target ? (
                    <p className="text-xs text-gray-500 mt-1">
                      {configFormData.targetObjects.length > 0
                        ? `${configFormData.targetObjects.length} object(s) selected`
                        : "Click to select objects"}
                    </p>
                  ) : (
                    <p className="text-xs text-gray-500 mt-1">
                      Please select a target first to load available objects
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="config-is-active" className="block text-sm font-medium text-gray-700 mb-1">
                    Status *
                  </label>
                  <select
                    id="config-is-active"
                    name="isActive"
                    value={configFormData.isActive}
                    onChange={(e) => {
                      const { name, value } = e.target;
                      setConfigFormData((prev) => ({
                        ...prev,
                        [name]: value === 'true',
                      }));
                    }}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>
              </div>



              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={isWizardMode ? onBack : handleCloseAddConfigModal}
                  className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
                >
                  {isWizardMode ? "← Back" : "Cancel"}
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-5 py-2.5 bg-blue-700 text-white rounded-lg font-medium hover:bg-blue-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {submitButtonText}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10001]">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto bg-red-100 rounded-full mb-4">
                <svg
                  className="w-6 h-6 text-red-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>

              <h3 className="text-lg font-bold text-gray-900 text-center mb-2">
                Delete Configuration?
              </h3>

              <p className="text-sm text-gray-600 text-center mb-6">
                This action cannot be undone. This will permanently delete the configuration from the system.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => {
                    setIsDeleteModalOpen(false);
                    setConfigToDelete(null);
                  }}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteConfig}
                  disabled={isDeleting}
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isDeleting && (
                    <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></span>
                  )}
                  {isDeleting ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

OrganizationConfigureModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  organization: PropTypes.shape({
    organizationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  onClose: PropTypes.func.isRequired,
  isWizardMode: PropTypes.bool,
  onBack: PropTypes.func,
};

export default OrganizationConfigureModal;